import React from 'react';
import { brandColors, PEAK_FROSTED_SKY_ANCHOR, PEAK_FROSTED_SKY_RAMP_HEX } from '../../data/brandColors';
import { CONSULTING_ERA_RAMPS } from '../../data/consultingEraRamps';
import { LEGACY_WAS_NOW, type LegacyWasNowRow } from '../../data/legacyWasNow.generated';
import { DataTable, type Column } from '../../components/ds';
import { ProductDesignPageHeader, ProductDesignSection, SwatchChip } from './ProductDesignDocPrimitives';

/**
 * The one home for legacy colors and marks (owner ruling 2026-10-01: everything
 * legacy lives in the Legacy section, and says it is deprecated). These sections
 * used to sit inside the current Colors, Brand identity and Logo system pages,
 * where they read as part of the standard. The route mounts DeprecatedBanner
 * above this page; each section repeats the word so a screenshot of any one of
 * them still says it.
 */

/** Small eyebrow label for a ramp group. */
const RampLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="mb-3 font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--p-label)]">
    {children}
  </h4>
);

const LegacyColorCard: React.FC<{ name: string; hex: string; alias?: string }> = ({ name, hex, alias }) => (
  <div className="overflow-hidden border border-[var(--p-hairline)]">
    <div className="h-16" style={{ backgroundColor: hex }} />
    <div className="bg-[var(--p-shade)] p-3">
      <div className="font-serif text-[15px] text-[var(--p-ink)]">{name}</div>
      <code className="font-mono text-[13px] text-[var(--mustard)]">{hex}</code>
      {alias && (
        <div className="mt-1 font-mono text-[13px] uppercase tracking-[0.16em] text-[var(--p-faint)]">{alias}</div>
      )}
    </div>
  </div>
);

const tokenCode = (value: string) => <code className="font-mono text-[13px] text-[var(--mustard)]">{value}</code>;

/** Was → now. The rows are generated from the same mapping the app's repaint sheet is built from. */
const WAS_NOW_COLUMNS: Column<LegacyWasNowRow>[] = [
  {
    key: 'was',
    header: 'Old color',
    width: '22%',
    render: (r) => (
      <>
        <div className="font-serif text-[15px] text-[var(--p-ink)]">{r.was}</div>
        <div className="font-mono text-[13px] text-[var(--p-faint)]">{r.families.join(' · ')}</div>
      </>
    ),
  },
  { key: 'paints', header: 'Paints', width: '14%' },
  { key: 'shades', header: 'Shades', width: '10%', cellClassName: 'font-mono text-[13px]' },
  {
    key: 'now',
    header: 'Now',
    width: '24%',
    render: (r) =>
      r.paper === r.night ? (
        tokenCode(r.paper)
      ) : (
        <>
          <div>Paper {tokenCode(r.paper)}</div>
          <div>Night {tokenCode(r.night)}</div>
        </>
      ),
  },
  {
    key: 'note',
    header: 'Why',
    render: (r) => r.note,
  },
];

const GRANITE = ['#F6F8FA', '#ECEFF3', '#DFE3EA', '#B7BCC6', '#9CA3AF', '#808894', '#555D6A', '#393C43', '#1C1E22'];
const SAGE = ['#F2F7F3', '#D9E8DC', '#B5D1BA', '#84B18C', '#6A9C73', '#5A7D5E', '#465F49', '#354738', '#1E2A20'];
const ALPINE = ['#E8F4EE', '#C6E1D2', '#A3CCB5', '#73AA8C', '#5E9876', '#497962', '#53675B', '#34423B', '#19241C'];

export const PrioritiesAILegacyPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Legacy colors and marks"
      eyebrow="Deprecated · not for new work"
      subtitle={
        <>
          The colors and the logo that came before the current design language. Everything on this
          page is deprecated. It is listed, not deleted, because parts of the app's code still name
          the colors. The app no longer paints them: each one is repainted onto a current token (the
          table below). New work uses the two current palettes, Paper and Night.
        </>
      }
    />

    <ProductDesignSection
      title="Deprecated · the first palette"
      hint="The first brand palette (source: src/data/brandColors.ts) and the Frosted Sky blue register. Older colours the app's code still uses — the sage green family, Electric Mustard, Luminous Coral, Alpine. Not for new work, and not to be deleted while the app depends on them."
    >
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LegacyColorCard name={brandColors.primary.name} hex={brandColors.primary.hex} alias="primary · slate-blue" />
        <LegacyColorCard name={brandColors.secondary.name} hex={brandColors.secondary.hex} alias="secondary" />
        <LegacyColorCard name={brandColors.accent.name} hex={brandColors.accent.hex} alias="accent · mustard" />
        <LegacyColorCard name={brandColors.coral.name} hex={brandColors.coral.hex} alias="coral" />
      </div>
    </ProductDesignSection>

    <ProductDesignSection
      title="Deprecated · Sage, Frosted Sky, Alpine"
      hint="Still in use: the sage family is the app's `primary` color class, about 5,500 uses in 553 files, and Alpine about 220 (measured 2026-10-01). Not for new work. Do not delete while the app depends on it."
    >
      <div className="space-y-8">
        <div>
          <RampLabel>
            {brandColors.primary.name} (slate-blue / primary) · anchor{' '}
            <code className="font-mono normal-case tracking-normal text-[var(--mustard)]">{brandColors.primary.hex}</code>
          </RampLabel>
          <div className="flex flex-wrap gap-2">
            {SAGE.map((h) => (
              <SwatchChip key={h} hex={h} title={`slate-blue · ${h}`} />
            ))}
          </div>
        </div>
        <div>
          <RampLabel>
            Frosted Sky — Peak blue register · anchor{' '}
            <code className="font-mono normal-case tracking-normal text-[var(--mustard)]">{PEAK_FROSTED_SKY_ANCHOR}</code>
          </RampLabel>
          <p className="mb-3 text-sm leading-relaxed text-[var(--text-ter)]">
            Chromatic blue for consulting collateral (Tailwind{' '}
            <code className="font-mono text-[var(--mustard)]">sky</code>). Distinct from the Sage
            primary above and from dark-shell{' '}
            <code className="font-mono text-[var(--mustard)]">--slate</code> (#3D4B6F).
          </p>
          <div className="flex flex-wrap gap-2">
            {PEAK_FROSTED_SKY_RAMP_HEX.map((h) => (
              <SwatchChip
                key={h}
                hex={h}
                title={`frosted-sky · ${h}`}
                ringClass={h === PEAK_FROSTED_SKY_ANCHOR ? 'ring-2 ring-sky-600/30' : 'ring-1 ring-black/10'}
              />
            ))}
          </div>
        </div>
        <div>
          <RampLabel>{brandColors.secondary.name}</RampLabel>
          <div className="flex flex-wrap gap-2">
            {ALPINE.map((h) => (
              <SwatchChip key={h} hex={h} title={`alpine-green · ${h}`} />
            ))}
          </div>
        </div>
      </div>
    </ProductDesignSection>

    <ProductDesignSection
      title="Deprecated · Electric Mustard and Luminous Coral"
      hint="Still in use: about 630 uses in the app's code (measured 2026-10-01). Not for new work — gold and clay replace them, and the diagrams moved to gold and clay on 2026-10-01. Do not delete them while the app depends on them."
    >
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <RampLabel>{brandColors.accent.name}</RampLabel>
          <div className="flex flex-wrap gap-2">
            {['#FEF8DF', '#FCEEB5', '#FAE270', '#E3B505', '#B89003'].map((h) => (
              <SwatchChip key={h} hex={h} ringClass={h === '#E3B505' ? 'ring-2 ring-[var(--mustard)]/40' : 'ring-1 ring-black/10'} />
            ))}
          </div>
        </div>
        <div>
          <RampLabel>{brandColors.coral.name}</RampLabel>
          <div className="flex flex-wrap gap-2">
            {['#FEEBEB', '#FBB8B8', '#F14646', '#F85E5E', '#960D0D'].map((h) => (
              <SwatchChip key={h} hex={h} ringClass={h === '#F85E5E' ? 'ring-2 ring-[var(--coral)]/35' : 'ring-1 ring-black/10'} />
            ))}
          </div>
        </div>
      </div>
    </ProductDesignSection>

    <ProductDesignSection
      title="Deprecated · Granite neutrals"
      hint="Still in use: 152 uses in 13 files (measured 2026-10-01). In both modes these classes are remapped onto the current tokens. Not for new work."
    >
      <div className="flex flex-wrap gap-2">
        {GRANITE.map((h) => (
          <SwatchChip key={h} hex={h} title={`granite · ${h}`} />
        ))}
      </div>
    </ProductDesignSection>

    {/* The full V1 corpus, rehomed from the retired /colors page (conflict №11).
        Kept because these ramps existed nowhere else and retiring that page
        would have made 149 swatches unreachable. */}
    <ProductDesignSection
      title="Deprecated · the full first-palette ramps"
      hint="Every ramp the retired /colors page carried, kept here so nothing was lost. Not for new work — kept because older decks, exports and parts of the app still cite these values."
    >
      <div className="flex flex-col gap-8">
        {CONSULTING_ERA_RAMPS.map((r) => (
          <div key={r.name}>
            <RampLabel>
              {r.name} <span className="opacity-60">· anchor {r.anchor}</span>
            </RampLabel>
            <div className="flex flex-wrap gap-2">
              {r.hexes.map((h) => (
                <SwatchChip
                  key={`${r.name}-${h}`}
                  hex={h}
                  title={`${r.name} · ${h}`}
                  ringClass={h === r.anchor ? 'ring-2 ring-[var(--mustard)]/50' : 'ring-1 ring-black/10'}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </ProductDesignSection>

    <ProductDesignSection
      title="Was → now · what the app paints instead"
      hint="The app's code still names these colors in about 16,000 places. A style sheet repaints every one of them onto a current token, in light and in dark, so no product screen shows an old color. Deprecated and archive pages are left alone: they are the record. These rows are generated from scripts/legacyColorMap.ts, the same table the sheet is built from, so this page cannot drift from what the app paints."
    >
      <DataTable
        columns={WAS_NOW_COLUMNS}
        rows={LEGACY_WAS_NOW}
        getRowKey={(r) => `${r.was}-${r.paints}-${r.shades}`}
        density="compact"
        bordered={false}
        caption="What each legacy color class paints now"
        captionHidden
      />
    </ProductDesignSection>

    <ProductDesignSection
      title="Deprecated · the earlier Peak Priorities logo"
      hint="Retired 2026-10-01. Kept on the record so older material can be recognized. Do not use in new work."
    >
      <div className="flex flex-wrap items-stretch gap-4">
        <div className="min-w-[200px] flex-1">
          <div className="flex min-h-[120px] items-center justify-center border border-[var(--p-hairline)] p-8" style={{ backgroundColor: '#FFFFFF' }}>
            <img src="/peakpriorities_logo.png" alt="The earlier Peak Priorities logo" style={{ height: 40 }} />
          </div>
        </div>
        <div className="min-w-[200px] flex-1">
          <div className="flex min-h-[120px] items-center border border-dashed border-[var(--p-gold)] bg-[var(--p-cream)] p-6">
            <p className="font-serif text-[15px] italic leading-relaxed text-[var(--p-ink-soft)]">
              The earlier Peak Priorities lockup: a different emblem, the name in title case, dark
              blue. It was shown on the Logo system page and in the app's top bar until the
              capitalized logo was confirmed as the current one.
            </p>
          </div>
        </div>
      </div>
      <p className="mt-3 font-mono text-xs text-[var(--text-ter)]">public/peakpriorities_logo.png</p>
    </ProductDesignSection>
  </>
);
